import {
  LANE_IDS,
  type GameEvent,
  type GameState,
  type GameStateV4,
  type GameStateV5,
  type PlayerId,
} from "../types";
import { getOpponentPlayerId } from "../types";
import { getActiveLandLaneIds, isActiveLandForPlayer } from "../selectors/formation";

function eventId(actionSequence: number, ordinal: number): string {
  return `event-${actionSequence}-${ordinal}`;
}

function populatedLanesWith(
  populatedLaneIds: readonly (typeof LANE_IDS)[number][],
  laneId: (typeof LANE_IDS)[number],
): readonly (typeof LANE_IDS)[number][] {
  return LANE_IDS.filter(
    (candidate) => candidate === laneId || populatedLaneIds.includes(candidate),
  );
}

/** Records the historical, not current-territory, founding seal. */
export function recordKingdomFoundation(
  state: Readonly<GameState>,
  playerId: PlayerId,
  laneId: (typeof LANE_IDS)[number],
  actionSequence: number,
  startingEventOrdinal: number,
): { readonly state: GameState; readonly events: readonly GameEvent[] } {
  if (state.schemaVersion !== 4 && state.schemaVersion !== 5) return { state, events: [] };
  const previous = state.kingdomProgress[playerId];
  const populatedLaneIds = populatedLanesWith(previous.populatedLaneIds, laneId);
  const complete = populatedLaneIds.length === LANE_IDS.length;
  if (
    previous.complete === complete &&
    previous.populatedLaneIds.length === populatedLaneIds.length &&
    previous.populatedLaneIds.every((candidate, index) => candidate === populatedLaneIds[index])
  ) {
    return { state, events: [] };
  }
  const events: GameEvent[] = [
    {
      type: "kingdom-progress-updated",
      eventId: eventId(actionSequence, startingEventOrdinal),
      actionSequence,
      playerId,
      laneId,
      populatedLaneIds,
      complete,
    },
  ];
  if (complete && !previous.complete) {
    events.push({
      type: "kingdom-completed",
      eventId: eventId(actionSequence, startingEventOrdinal + events.length),
      actionSequence,
      playerId,
    });
  }
  return {
    state: {
      ...state,
      kingdomProgress: { ...state.kingdomProgress, [playerId]: { populatedLaneIds, complete } },
    },
    events,
  };
}

/** Creates/cancels the single public warning after a complete authoritative action. */
export function reconcileNoTerritoryWarning(
  state: Readonly<GameState>,
  actionSequence: number,
  startingEventOrdinal: number,
): { readonly state: GameState; readonly events: readonly GameEvent[] } {
  if (
    (state.schemaVersion !== 4 && state.schemaVersion !== 5) ||
    state.status.type === "finished"
  ) {
    return { state, events: [] };
  }
  const current = state.noTerritoryWarning;
  if (current !== null) {
    const threatenedHasLand = getActiveLandLaneIds(state, current.threatenedPlayerId).length > 0;
    const threateningHasLand = getActiveLandLaneIds(state, current.threateningPlayerId).length > 0;
    if (!threatenedHasLand && threateningHasLand) {
      return { state, events: [] };
    }
    const reason = threatenedHasLand ? "territory-restored" : "threatening-player-lost-territory";
    return {
      state: { ...state, noTerritoryWarning: null },
      events: [
        {
          type: "no-territory-warning-cleared",
          eventId: eventId(actionSequence, startingEventOrdinal),
          actionSequence,
          threatenedPlayerId: current.threatenedPlayerId,
          reason,
        },
      ],
    };
  }

  for (const threatenedPlayerId of ["player-1", "player-2"] as const) {
    const threateningPlayerId = getOpponentPlayerId(threatenedPlayerId);
    if (
      state.kingdomProgress[threateningPlayerId].complete &&
      getActiveLandLaneIds(state, threateningPlayerId).length > 0 &&
      getActiveLandLaneIds(state, threatenedPlayerId).length === 0
    ) {
      return {
        state: {
          ...state,
          noTerritoryWarning: {
            threatenedPlayerId,
            threateningPlayerId,
            createdActionSequence: actionSequence,
            createdTurnNumber: state.turnNumber,
            deadlineTurnNumber: null,
          },
        },
        events: [
          {
            type: "no-territory-warning-started",
            eventId: eventId(actionSequence, startingEventOrdinal),
            actionSequence,
            threatenedPlayerId,
            threateningPlayerId,
          },
        ],
      };
    }
  }
  return { state, events: [] };
}

/** Arms the response deadline only as the threatened player's later Land Phase begins. */
export function armNoTerritoryDeadline(state: Readonly<GameState>): GameState {
  if (state.schemaVersion !== 4 || state.noTerritoryWarning === null) return state;
  const warning = state.noTerritoryWarning;
  if (
    warning.deadlineTurnNumber !== null ||
    state.activePlayerId !== warning.threatenedPlayerId ||
    state.turnNumber <= warning.createdTurnNumber ||
    state.phase !== "land"
  ) {
    return state;
  }
  return { ...state, noTerritoryWarning: { ...warning, deadlineTurnNumber: state.turnNumber } };
}

/** Applies the explicitly delayed loss only when its armed Land Phase actually ends. */
export function resolveNoTerritoryDeadline(
  state: Readonly<GameState>,
  actionSequence: number,
  startingEventOrdinal: number,
): { readonly state: GameState; readonly events: readonly GameEvent[] } | null {
  if (state.schemaVersion !== 4 || state.noTerritoryWarning === null) return null;
  const warning = state.noTerritoryWarning;
  if (
    state.phase !== "land" ||
    state.activePlayerId !== warning.threatenedPlayerId ||
    warning.deadlineTurnNumber !== state.turnNumber ||
    isActiveLandForPlayer(state, warning.threatenedPlayerId, "left") ||
    isActiveLandForPlayer(state, warning.threatenedPlayerId, "center") ||
    isActiveLandForPlayer(state, warning.threatenedPlayerId, "right")
  ) {
    return null;
  }
  const nextState: GameStateV4 = {
    ...state,
    phase: "finished",
    status: {
      type: "finished",
      winnerPlayerId: warning.threateningPlayerId,
      reason: "no-territory",
    },
    noTerritoryWarning: null,
  };
  return {
    state: nextState,
    events: [
      {
        type: "match-finished",
        eventId: eventId(actionSequence, startingEventOrdinal),
        actionSequence,
        winnerPlayerId: warning.threateningPlayerId,
        reason: "no-territory",
      },
    ],
  };
}

/** Public beta resolves the threatened player's response window through End Turn. */
export function resolvePublicBetaNoTerritoryOnEndTurn(
  state: Readonly<GameStateV5>,
  actionSequence: number,
  startingEventOrdinal: number,
): { readonly state: GameStateV5; readonly events: readonly GameEvent[] } | null {
  const warning = state.noTerritoryWarning;
  if (
    warning === null ||
    state.activePlayerId !== warning.threatenedPlayerId ||
    getActiveLandLaneIds(state, warning.threatenedPlayerId).length > 0
  ) {
    return null;
  }

  return {
    state: {
      ...state,
      phase: "finished",
      status: {
        type: "finished",
        winnerPlayerId: warning.threateningPlayerId,
        reason: "no-territory",
      },
      noTerritoryWarning: null,
    },
    events: [
      {
        type: "match-finished",
        eventId: eventId(actionSequence, startingEventOrdinal),
        actionSequence,
        winnerPlayerId: warning.threateningPlayerId,
        reason: "no-territory",
      },
    ],
  };
}
